import { Component, ViewChild, ElementRef, signal, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { AlertType, ButtonPurpose } from '@mhe/ngx-shared';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { setAnnotationAriaLabel } from '../utils/set-annotation-aria-label';
import { BonsaiTreeComponent } from '@mhe/ngx-bonsai-reader';

import { map, takeUntil, tap } from 'rxjs/operators';
import * as errorsQuery from '@mhe/reader/global-store/errors/errors.selectors';
import * as annotationsQuery from '@mhe/reader/global-store/annotations/annotations.selectors';
import * as annotationsActions from '@mhe/reader/global-store/annotations/annotations.actions';
import {
  AnnotationListBonsaiNode,
  ApiAnnotation,
  ROOT_BONSAI_NODE,
} from '@mhe/reader/models';
import {
  annotationSelected,
  getAnnotationsForLabel,
  exportNotes,
  noteDelete,
  notesBatchDelete,
} from './state/note-list.actions';
import { NoteListStore } from './state/note-list.store';
import { DropdownState, AnnotationType } from '../annotation.data';
import { BatchDeleteState } from '../utils/annotation-list.state.abstract';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationModalComponent, ConfirmationModalData } from '../../modals/confirmation-modal';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'rdrx-note-list',
  templateUrl: './note-list.component.html',
  styleUrls: ['./note-list.component.scss'],
  host: { 'data-automation-id': 'note-list-modal' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class NoteListComponent implements OnDestroy {
  @ViewChild(BonsaiTreeComponent, { read: ElementRef }) tree: ElementRef;
  readonly rootNodeId = ROOT_BONSAI_NODE;
  readonly truncateLimit = 100;
  ButtonPurpose = ButtonPurpose;
  AlertType = AlertType;
  readonly destroy$ = new Subject<boolean>();
  readonly sessionEnded$ = this.store.pipe(select(errorsQuery.hasSessionEnded));
  readonly emptyTree$ = combineLatest([
    this.noteListStore.tree$,
    this.sessionEnded$,
  ]).pipe(
    map(
      ([tree, hasSessionEnded]) =>
        !hasSessionEnded && Object.keys(tree).length === 1,
    ), // one because the root node
  );

  nodeTree$ = this.noteListStore.tree$.pipe(
    tap(treeData => (this.treeDataCopy = treeData)),
    tap(() => {
      if (this.allAnnotationsSelected) {
        this.notesSelected = [];
        this.handleAllAnnotationsSelected(true);
      }
    }),
  );

  readonly userActionBatchDelete$ = this.noteListStore.batchDeleteState$.pipe(
    takeUntil(this.destroy$),
    tap(state => {
      if (state === BatchDeleteState.SUCCESS) {
        this.handleCancelClick();
      }
    }),
  ).subscribe();

  exportStatus$ = this.store.select(annotationsQuery.annotationsExportStatus);

  treeDataCopy = {};
  notesDropDownState = signal<DropdownState>(DropdownState.ALL);
  isEditMode = false;
  notesSelected: string[] = [];
  headerType = AnnotationType.NOTE;
  allAnnotationsSelected = false;

  constructor(
    readonly noteListStore: NoteListStore,
    readonly store: Store,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
  ) {}

  get isFooterBtnDisabled(): boolean {
    return !!this.notesSelected.length;
  }

  handleNodeClick(node: AnnotationListBonsaiNode): void {
    if (this.isEditMode) {
      return;
    }
    if (node.annotation) {
      this.noteListStore.dispatch(
        annotationSelected({ annotation: node.annotation }),
      );
    } else {
      this.noteListStore.dispatch(getAnnotationsForLabel({ spineID: node.id }));
      this.noteListStore.toggleExpandedNode(node.id);
    }
  }

  delete(annotation: ApiAnnotation): void {
    if (annotation) {
      this.noteListStore.dispatch(noteDelete({ annotation }));
    }
  }

  toggleNodeExpanded(nodeId: string): void {
    this.noteListStore.dispatch(getAnnotationsForLabel({ spineID: nodeId }));
    this.noteListStore.toggleExpandedNode(nodeId);
  }

  isCollapsible(node: AnnotationListBonsaiNode): boolean {
    return node.collapsible ?? true;
  }

  isLoading(node: AnnotationListBonsaiNode): boolean {
    return node.childIds?.length === 0;
  }

  beginExport(): void {
    const dialogRef = this.openConfirmationDialog();
    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$),
      tap(result => {
        if (result) {
          this.dispatchExportAnnotations();
          this.completeExport();
        }
      }),
    ).subscribe();
  }

  private openConfirmationDialog(): MatDialogRef<ConfirmationModalComponent, boolean> {
    const data: ConfirmationModalData = {
      title: this.translate.instant('export.export'),
      content: this.translate.instant('export.complete'),
      closeText: this.translate.instant('shared.cancel'),
      confirmText: this.translate.instant('export.download'),
    };
    return this.dialog.open(ConfirmationModalComponent, {
      data,
      ariaLabelledBy: 'confirmation-modal-h2',
    });
  }

  private dispatchExportAnnotations(): void {
    this.store.dispatch(
      annotationsActions.fetchAnnotationsForAllSpinesByType({
        annotationType: 'note',
      }),
    );
  }

  completeExport(): void {
    this.noteListStore.dispatch(exportNotes());
  }

  // Set aria label on bonsai treeitem tag
  handleTextChange(text: string, node: AnnotationListBonsaiNode): void {
    if (node.annotation) {
      setAnnotationAriaLabel(this.tree, text, node);
    }
  }

  isNodeChecked({ id }: AnnotationListBonsaiNode): boolean {
    return !!this.notesSelected.find(hlId => hlId === id);
  }

  handleDropdownState(state: DropdownState): void {
    this.notesDropDownState.set(state);
  }

  handleEditMode(isEditMode: boolean): void {
    this.isEditMode = isEditMode;
    this.noteListStore.batchDeleteIdle();
  }

  handleAllAnnotationsSelected(isChecked: boolean): void {
    this.allAnnotationsSelected = true;
    this.notesDropDownState() === DropdownState.ALL
      ? this.highlightSelectAll(isChecked)
      : this.highlightSelectDetached(isChecked);
  }

  highlightSelectAll(isChecked: boolean): void {
    if (!isChecked) {
      this.notesSelected = [];
      return;
    }
    this.notesSelected = Object.keys(this.treeDataCopy)
      .map(key => this.treeDataCopy[key].id)
      .filter(Boolean);
  }

  highlightSelectDetached(isChecked: boolean): void {
    if (!isChecked) {
      this.notesSelected = [];
      return;
    }
    this.notesSelected = Object
      .keys(this.treeDataCopy)
      .filter(key => Boolean(this.treeDataCopy[key].annotation?.orphan))
      .map(key => this.treeDataCopy[key].id);
  }

  handleCancelClick(): void {
    this.isEditMode = false;
    this.allAnnotationsSelected = false;
    this.notesSelected = [];
  }

  handleDeleteClick(): void {
    if (this.notesSelected.length > 0) {
      this.noteListStore.batchDeleteStart();

      const annotations = this.notesSelected
        .map(id => this.treeDataCopy[id].annotation)
        .filter(Boolean);
      this.noteListStore.dispatch(
        notesBatchDelete({ data: { annotations, type: AnnotationType.NOTE } }),
      );
    }
  }

  handleCheckAnnotation({ id, collapsible, childIds }: AnnotationListBonsaiNode): void {
    // Handle Chapter Level
    if (collapsible && childIds) {
      if (this.notesSelected.some(annotationId => annotationId === id)) {
        this.notesSelected = this.notesSelected.filter(hlId => !childIds.includes(hlId));
        this.notesSelected = this.notesSelected.filter(hlId => hlId !== id);
        return;
      }
      this.notesSelected = [
        ...this.notesSelected,
        ...childIds.map(childId => this.treeDataCopy[childId].id),
        id,
      ];
      return;
    }
    // Handle individuals
    if (this.notesSelected.find(hlId => hlId === id)) {
      this.notesSelected = this.notesSelected.filter(hlId => hlId !== id);
      return;
    }
    this.notesSelected = [
      ...this.notesSelected,
      id,
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
